
import React, { useEffect, useMemo, useState } from 'react'
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl.js'
import { useIntl } from 'react-intl';
import { Divider, Grid, makeStyles } from '@material-ui/core';
import styles from './styles.js';
import Header from './Header/index.js';
import { AlertDialog, AlertMessages, FormActionButtons, Loading, TabPanel, TabsContainer } from '../../../components/index.js';
import { findIndex } from 'lodash';
import { useHistory, useParams } from 'react-router';
import Overview from '../Details/Overview/index.js';
import Apis from '../Details/Apis/index.js';
import Applications from '../Details/Applications/index.js';
import { useDispatch, useSelector } from 'react-redux';
import {
  getApiGroupAddEditDeleteErrors,
  getApiGroupsLoading,
  getAvailableApisTotalElementsForGroups,
  getCreateApiGroup,
  getDeleteApiGroup,
  getEditApiGroup,
  getSingleApiGroup,
  getUpdateApisForApiGroups,
  getUpdateApisForApiGroupsErrors,
} from '../../../reducers/apiGroups/index.js';
import { createApiGroup, editApiGroup, fetchApiGroup, fetchApisForApiGroups, updateApisForApiGroups } from '../../../actions/apiGroups/index.js';
import { getApplicationsListTotalElements } from '../../../reducers/application/index.js';
import { getIsApiOwner, getIsPortalAdmin } from '../../../reducers/portalConfig/index.js';
import { ALERT_SUCCESS, GRID_ROWS_PER_PAGE_DEFAULT_OPTION, PROCEED_TEXT } from '../../../constants/index.js';
import { ROUTES } from '../../../constants/routes.js';

export const CHANGE_MODE_REMOVE_ACCESS = 'CHANGE_MODE_REMOVE_ACCESS';
export const CHANGE_MODE_ADD_ACCESS = 'CHANGE_MODE_ADD_ACCESS';

export const ApiGroupDetails = () => {
  const [currentTab, setCurrentTab] = useState('add-api-group-overview-tab');
  const [redirectTabValue, setRedirectTabValue] = useState();
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [assignedApis, setAssignedApis] = useState([]);
  const [unAssignedApis, setUnAssignedApis] = useState();
  const [apiGroupName, setApiGroupName] = useState('');
  const [apiGroupDescription, setApiGroupDescription] = useState('');
  const [filterByApiName, setFilterByApiName] = useState('');
  const [changeMode, setChangeMode] = useState(CHANGE_MODE_REMOVE_ACCESS);
  const [selectedRows, setSelectedRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [alertMessages, setAlertMessages] = useState({
    message: '',
    variant: '',
    status: false,
  })

  const availableApisTotalElements = useSelector(getAvailableApisTotalElementsForGroups);
  const applicationsTotalElements = useSelector(getApplicationsListTotalElements);
  const isLoading = useSelector(getApiGroupsLoading);
  const isApiGroupCreationSuccess = useSelector(getCreateApiGroup);
  const apiGroupData = useSelector(getSingleApiGroup);
  const apiGroupAddEditDeleteErrors = useSelector(getApiGroupAddEditDeleteErrors);
  const isApiGroupUpdateSuccess = useSelector(getEditApiGroup);
  const isApiGroupDeleteSuccess = useSelector(getDeleteApiGroup);
  const isApiAccessSuccess = useSelector(getUpdateApisForApiGroups);
  const apiAccessErrors = useSelector(getUpdateApisForApiGroupsErrors);
  const isApiOwner = useSelector(getIsApiOwner);
  const isPortalAdmin = useSelector(getIsPortalAdmin);

  const params = useParams();
  const apiGroupUuidFromEditFlow = params?.apiGroupUuid;
  const apiGroupUuid = isApiGroupCreationSuccess && isApiGroupCreationSuccess?.uuid ||
    apiGroupUuidFromEditFlow;

  const payload = {
    name: apiGroupName,
    description: apiGroupDescription,
    status: apiGroupData?.status || 'Enabled',
  }

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const assignedApisPayload = assignedApis?.map(apiUuid => {
    return {
      uuid: apiUuid,
    }
  });

  const unAssignedApisFiltered = unAssignedApis && unAssignedApis?.filter(apiUuid =>
    !assignedApis?.includes(apiUuid)) || [];
  const unAssignedApisPayload = unAssignedApisFiltered && unAssignedApisFiltered?.map(apiUuid => {
    return {
      uuid: apiUuid,
    }
  });

  const tabItems = [
    {
      id: 'overview',
      tabId: 'add-api-group-overview-tab',
      label: getI18nFormattedMessage('label.overview'),
      visible: true,
    },
    {
      id: 'apis',
      tabId: 'add-api-group-apis-tab',
      label: getI18nFormattedMessage('label.apis'),
      visible: true,
      disabled: !apiGroupUuid,
    },
    {
      id: 'applications',
      tabId: 'add-api-group-applications-tab',
      label: getI18nFormattedMessage('label.application.list.page.title'),
      visible: true,
      disabled: !apiGroupUuid,
    },
  ];

  useEffect(() => {
    setApiGroupName(apiGroupData?.name);
    setApiGroupDescription(apiGroupData?.description);
  }, [currentTab]);

  const changeActiveTab = (tabId) => {
    setCurrentTab(tabId);
    const id = findIndex(tabItems, tab => tab.tabId === tabId);
    if (apiGroupUuid) {
      history.push(`/admin/api-groups/details/${apiGroupUuid}/${tabItems[id].id}`);
    } else {
      history.push(`/admin/api-groups/add/${tabItems[id].id}`);
    }
  };

  const handleTabChange = (event) => {
    const tabId = event.currentTarget.id;
    let currentTabDirty = false;
    if (currentTab === 'add-api-group-overview-tab') {
      currentTabDirty = isOverviewTabDirty;
    } else if (currentTab === 'add-api-group-apis-tab') {
      currentTabDirty = isApisTabDirty;
    }
    if (currentTabDirty) {
      setRedirectTabValue(tabId);
      setShowUnsavedDialog(true);
    } else {
      changeActiveTab(tabId);
    }
  };

  const onDialogClose = () => {
    setShowUnsavedDialog(false);
  };
  const onDialogSubmit = () => {
    setShowUnsavedDialog(false);
    changeActiveTab(redirectTabValue);
  };

  useEffect(() => {
    setApiGroupName(apiGroupData?.name);
    setApiGroupDescription(apiGroupData?.description);
  }, [apiGroupData]);

  const compareArrays = (arr1, arr2) => {
    if (arr1?.length !== arr2?.length) return true;

    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();

    for (let i = 0; i < sortedArr1.length; i++) {
      if (sortedArr1[i] !== sortedArr2[i]) return true;
    }
    return false;
  };

  const isOverviewTabDirty = useMemo(() => {
    if (!apiGroupUuid && apiGroupName?.trim()) return true;
    if (apiGroupUuid && apiGroupName?.trim() !== apiGroupData?.name) return true;
    if (apiGroupUuid && apiGroupDescription?.trim() &&
      apiGroupDescription?.trim() !== apiGroupData?.description) return true;
    return false;
  }, [apiGroupName, apiGroupData, apiGroupDescription]);

  const isApisTabDirty = useMemo(() => {
    if (apiGroupUuid) {
      return compareArrays(assignedApis, unAssignedApis)
    }
    return false;
  }, [apiGroupUuid, assignedApis, unAssignedApis]);

  const decideAccessModePayload = useMemo(() => {
    if (changeMode === CHANGE_MODE_ADD_ACCESS) {
      return assignedApisPayload;
    } else if (changeMode === CHANGE_MODE_REMOVE_ACCESS) {
      return unAssignedApisPayload;
    }
  }, [changeMode, assignedApisPayload, unAssignedApisPayload])

  const handleSaveApiGroup = () => {
    setAlertMessages({
      variant: "",
      message: "",
      status: false,
    })
    if (apiGroupUuid && currentTab === "add-api-group-apis-tab" && isApisTabDirty) {
      setFilterByApiName('');
      setSelectedRows([]);
      setPage(0);
      setRowsPerPage(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
      dispatch(updateApisForApiGroups(decideAccessModePayload, apiGroupUuid, changeMode));
    } else if (apiGroupUuid && !isApisTabDirty) {
      payload.uuid = apiGroupUuid;
      dispatch(editApiGroup(payload, apiGroupUuid));
    } else {
      dispatch(createApiGroup(payload));
    }
  }

  const handleCancel = () => {
    const isOverviewTab = currentTab === "add-api-group-overview-tab";
    const isAddAccessMode = changeMode === CHANGE_MODE_ADD_ACCESS;
    const isRemoveAccessMode = changeMode === CHANGE_MODE_REMOVE_ACCESS;

    const resetApiGroupSettings = () => {
      setFilterByApiName("");
      setPage(0);
      setRowsPerPage(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
    };

    const fetchApis = (status) => {
      dispatch(fetchApisForApiGroups(apiGroupUuid, {
        apiGroupApiAccessStatus: status,
        page: 0,
        size: GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
        name: "",
      }));
      setSelectedRows([]);
    };

    if (isApisTabDirty && !isOverviewTab) {
      resetApiGroupSettings();
      fetchApis(isRemoveAccessMode ? "ASSIGNED" : "NOT_ASSIGNED");
    } else if (isAddAccessMode && !isApisTabDirty) {
      resetApiGroupSettings();
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
      fetchApis("ASSIGNED");
    } else if (apiGroupUuid) {
      dispatch(fetchApiGroup(apiGroupUuid));
      history.push(`/admin/api-groups/details/${apiGroupUuid}`);
    } else {
      window.location.href = `/admin/api-groups`;
    }
  };

  useEffect(() => {
    if (isApiGroupCreationSuccess) {
      history.push(`/admin/api-groups/details/${apiGroupUuid}`);
    }
  }, [isApiGroupCreationSuccess]);

  useEffect(() => {
    if (apiGroupUuid) {
      return dispatch(fetchApiGroup(apiGroupUuid));
    }
  }, [apiGroupUuid]);

  useEffect(() => {
    if (isApiGroupUpdateSuccess) {
      return dispatch(fetchApiGroup(apiGroupUuid));
    }
  }, [isApiGroupUpdateSuccess]);

  const showAlertMessages = (variant, message) => {
    setAlertMessages({
      status: true,
      variant: variant,
      message: message,
    })
  }

  useEffect(() => {
    if (apiGroupAddEditDeleteErrors) {
      showAlertMessages("error", apiGroupAddEditDeleteErrors)
    }
  }, [apiGroupAddEditDeleteErrors])

  useEffect(() => {
    if (apiAccessErrors) {
      showAlertMessages("error", apiAccessErrors)
    }
  }, [apiAccessErrors])

  const apiGroupCreationSuccessMessage = intl.getI18nMessage("label.api.groups.creation.success.message");
  const apiGroupUpdateSuccessMessage = intl.getI18nMessage("label.api.groups.update.success.message");
  const apiGroupDeleteSuccessMessage = intl.getI18nMessage("label.api.groups.delete.success.message");

  useEffect(() => {
    if (isApiAccessSuccess) {
      showAlertMessages("success", apiGroupUpdateSuccessMessage)
    }
  }, [isApiAccessSuccess])

    useEffect(() => {
      if (isApiGroupCreationSuccess) {
        showAlertMessages("success", apiGroupCreationSuccessMessage)
      }
    }, [isApiGroupCreationSuccess])

      useEffect(() => {
        if (isApiGroupUpdateSuccess) {
          showAlertMessages("success", apiGroupUpdateSuccessMessage)
        }
      }, [isApiGroupUpdateSuccess])

      useEffect(() => {
        if (isApiGroupDeleteSuccess) {
          showAlertMessages("success", apiGroupDeleteSuccessMessage)
        }
      }, [isApiGroupDeleteSuccess])

  useEffect(() => {
    if (isApiGroupDeleteSuccess) {
      if (alertMessages.message === apiGroupDeleteSuccessMessage) {
        window.location.href = ROUTES.apiGroups.path;
      }
    }
  }, [isApiGroupDeleteSuccess, alertMessages.message, apiGroupDeleteSuccessMessage])

  useEffect(() => {
    if (!isApiOwner && !isPortalAdmin) {
      history.push('/404');
    }
  }, [isApiOwner, isPortalAdmin])

  const isApisAddMode = apiGroupUuid && currentTab === "add-api-group-apis-tab" && changeMode === CHANGE_MODE_ADD_ACCESS;

  return (
    <div className={classes.content}>
      {isLoading && <Loading pageLoader />}
      <AlertDialog
        isOpen={showUnsavedDialog}
        title={intl.getI18nMessage('label.dialog.unsaved.changes.title')}
        description={intl.getI18nMessage('label.dialog.unsaved.changes.description')}
        submitText={PROCEED_TEXT}
        cancelText={intl.getI18nMessage('label.dialog.unsaved.changes.cancel')}
        onClose={onDialogClose}
        onSubmit={onDialogSubmit}
        onCancel={onDialogClose}
        dialogId={'api-plans-unsaved-changes'}
      />
      {
        alertMessages?.status &&
        <AlertMessages
          id="add-api-group-client-snackbar"
          className={classes.alertMessagesContainer}
          message={alertMessages.message}
          variant={alertMessages.variant}
          onClose={() => setAlertMessages({ ...alertMessages, status: false })}
          shouldAutoCloseSuccessMessages={alertMessages.variant === ALERT_SUCCESS}
        />
      }
      <Header
        intl={intl}
        classes={classes}
        apiGroupUuid={apiGroupUuid}
        setApiGroupName={setApiGroupName}
        apiGroupName={apiGroupName}
        apiGroupNameFromApi={apiGroupData?.name}
        status={apiGroupData?.status}
        payload={payload}
        setAlertMessages={setAlertMessages}
      />
      <Grid container className={classes.gridContainer}>
        <TabsContainer
          id="api-group-details-tab-container"
          tabValue={currentTab}
          orientation="horizontal"
          tabItems={tabItems}
          handleTabChange={handleTabChange}
          tabActiveClass={classes.tabActive}
          tabIndicatorClass={classes.tabIndicator}
          useTabId
        />
      </Grid>
      <Divider className={classes.divider} />
      <Grid container className={classes.bodyContent}>
        <TabPanel visible={currentTab === 'add-api-group-overview-tab'} id='add-api-group-overview-tab'>
          <Overview
            setCurrentTab={setCurrentTab}
            apisCount={availableApisTotalElements}
            applicationsCount={applicationsTotalElements}
            setApiGroupDescription={setApiGroupDescription}
            apiGroupDescription={apiGroupDescription}
            classes={classes}
            apiGroupUuid={apiGroupUuid}
          />
        </TabPanel>
        <TabPanel visible={currentTab === 'add-api-group-apis-tab'} id='add-api-group-apis-tab'>
          {
            apiGroupUuid &&
            <Apis
              assignedApis={assignedApis}
              setAssignedApis={setAssignedApis}
              setCurrentTab={setCurrentTab}
              apiGroupData={apiGroupData}
              apiGroupUuid={apiGroupUuid}
              changeMode={changeMode}
              setChangeMode={setChangeMode}
              unAssignedApis={unAssignedApis}
              setUnAssignedApis={setUnAssignedApis}
              filterByApiName={filterByApiName}
              setFilterByApiName={setFilterByApiName}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              isApisTabDirty={isApisTabDirty}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          }
        </TabPanel>
        <TabPanel visible={currentTab === 'add-api-group-applications-tab'} id='add-api-group-applications-tab'>
          <Applications
            setCurrentTab={setCurrentTab}
            apiGroupUuid={apiGroupUuid}
          />
        </TabPanel>
      </Grid>
      {
        (isOverviewTabDirty || isApisTabDirty || isApisAddMode) && (
          <FormActionButtons
            id='add-api-group-action-buttons'
            nextText={getI18nFormattedMessage('label.save.button')}
            onNextClick={handleSaveApiGroup}
            onCancelClick={handleCancel}
            showCancel
            isContainerFooter
            isDisabled={!isApisTabDirty && apiGroupUuid && currentTab === "add-api-group-apis-tab"}
          />
        )
      }
    </div>
  )
}

export default ApiGroupDetails;
